import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Save, Trash2, ShieldCheck, User, UserCheck, UserX, RefreshCw } from "lucide-react";
import { toast, Toaster } from "sonner";
import PageHeader from "@/components/PageHeader";
import DriveBackupPanel from "@/components/DriveBackupPanel";
import GoogleBusinessPanel from "@/components/GoogleBusinessPanel";
import ChatbotConfigPanel from "@/components/ChatbotConfigPanel";
import StagePermissionsPanel from "@/components/StagePermissionsPanel";
import ListinoPriceAuditPanel from "@/components/ListinoPriceAuditPanel";
import AutoHealStatsPanel from "@/components/AutoHealStatsPanel";
import GhlResiliencePanel from "@/components/GhlResiliencePanel";
import GhlWriteQueuePanel from "@/components/GhlWriteQueuePanel";
import McpConnectorPanel from "@/components/McpConnectorPanel";

const API = window.location.origin;

const ALL_PAGES = [
  { id: "piano-cura", label: "Piano di Cura" },
  { id: "piani-cura", label: "Pipeline" },
  { id: "chiama-paziente", label: "Chiama Paziente" },
  { id: "confronto-opportunita", label: "Report Opportunità" },
  { id: "dashboard", label: "Dashboard" },
  { id: "pazienti-ricorrenti", label: "Pazienti Ricorrenti" },
  { id: "matching-appuntamenti", label: "Multiappuntamenti" },
  { id: "matching", label: "Matching Contatti" },
  { id: "appuntamenti-duplicati", label: "Appuntamenti Duplicati" },
  { id: "feedback", label: "Feedback" },
  { id: "monitor", label: "Monitor" },
  { id: "video-tutorial", label: "Video Tutorial" },
  { id: "storico-stage", label: "Pannello Assicurazioni" },
  { id: "confronto-listini", label: "Confronto Listini" },
];

const ROLE_STYLES = {
  admin: { bg: "bg-purple-100", text: "text-purple-700", icon: ShieldCheck, iconColor: "text-purple-600" },
  user: { bg: "bg-blue-100", text: "text-blue-700", icon: User, iconColor: "text-blue-600" },
};

export default function AdminPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user" });
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncingGhl, setIsSyncingGhl] = useState(false);

  const handleSyncGhl = async () => {
    if (isSyncingGhl) return;
    const confirmed = window.confirm(
      "Vuoi ricaricare la cache GHL contatti? L'operazione richiede 3-5 minuti."
    );
    if (!confirmed) return;
    setIsSyncingGhl(true);
    toast.info("Sincronizzazione GHL avviata… potrebbe richiedere qualche minuto");
    try {
      const { data } = await axios.post(
        `${API}/api/ghl/contacts/sync-cache`,
        {},
        { withCredentials: true, timeout: 600000 }
      );
      toast.success(
        data?.cached_contacts
          ? `Cache GHL aggiornata: ${data.cached_contacts} contatti`
          : "Sincronizzazione completata"
      );
    } catch (err) {
      if (err.response?.status === 409) {
        toast.warning("Sincronizzazione già in corso");
      } else {
        toast.error(err.response?.data?.detail || "Errore durante la sincronizzazione");
      }
    } finally {
      setIsSyncingGhl(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await fetchUsers();
      toast.success("Modifiche salvate");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/users`, { withCredentials: true });
      setUsers(res.data.users || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento utenti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) { toast.error("Email e nome sono obbligatori"); return; }
    setIsCreating(true);
    try {
      // NUOVO FLUSSO INVITO: il backend NON richiede più la password —
      // genera un invite token (24h, single-use) e invia l'email
      // appropriata: link `/accept-invite?token=…` per email non-Gmail,
      // welcome con "Accedi con Google" per email @gmail.com.
      const payload = { email: form.email, name: form.name, role: form.role };
      const { data } = await axios.post(`${API}/api/admin/users`, payload, { withCredentials: true });
      const kind = data?.invite_kind || "";
      const sent = data?.invite_sent;
      if (kind === "oauth" && sent) {
        toast.success("Utente Gmail creato. Email inviata con istruzioni per 'Accedi con Google'");
      } else if (kind === "password" && sent) {
        toast.success("Utente creato. Email di invito inviata (valida 24h)");
      } else if (kind === "password" && !sent) {
        toast.warning("Utente creato, ma l'email NON è stata inviata. Verifica la configurazione SMTP.");
      } else {
        toast.success("Utente creato");
      }
      setForm({ email: "", name: "", password: "", role: "user" });
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    } finally {
      setIsCreating(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/api/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      let msg;
      if (newRole === "admin") {
        msg = "Ruolo aggiornato → admin. Permessi pagine, indiretti shortcut e gestione listino assegnati. La sessione dell'utente si aggiornerà entro 60 secondi.";
      } else if (newRole === "user_plus") {
        msg = "Ruolo aggiornato → user+. Verrà applicata la matrice permessi USER+ (configurabile in 'Permessi USER per stage GHL' nella tab USER+). La sessione si aggiornerà entro 60 secondi.";
      } else {
        msg = "Ruolo aggiornato → user. Pagine di default ripristinate, feature flags disattivate. La sessione dell'utente si aggiornerà entro 60 secondi.";
      }
      toast.success(msg, { duration: 6000 });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handlePageToggle = async (userId, pageId, currentPages) => {
    const pages = currentPages || [];
    const newPages = pages.includes(pageId) ? pages.filter((p) => p !== pageId) : [...pages, pageId];
    try {
      await axios.put(`${API}/api/admin/users/${userId}/pages`, { allowed_pages: newPages }, { withCredentials: true });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await axios.put(`${API}/api/admin/users/${userId}/toggle-active`, {}, { withCredentials: true });
      toast.success("Stato aggiornato");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  // Toggle del flag "Indiretti rapidi" (POST /flags). Permette agli utenti
  // standard di vedere i bottoni "Ind.O." / "Ind.R.C." in Archivio Piani di
  // Cura per duplicare un piano assicurazione come opp Indiretti.
  const handleToggleIndirettiShortcut = async (user) => {
    try {
      await axios.put(
        `${API}/api/admin/users/${user.user_id}/flags`,
        { indiretti_shortcut: !user.indiretti_shortcut },
        { withCredentials: true },
      );
      toast.success(
        !user.indiretti_shortcut
          ? "Indiretti rapidi attivati"
          : "Indiretti rapidi disattivati",
      );
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  // Toggle flag "Gestione listino": permette all'utente di accedere alla
  // pagina /gestione-listino per modificare prezzi/aggiungere prestazioni
  // (richiesta utente 04/06/2026).
  const handleToggleManageTreatments = async (user) => {
    try {
      await axios.put(
        `${API}/api/admin/users/${user.user_id}/flags`,
        { manage_treatments: !user.manage_treatments },
        { withCredentials: true },
      );
      toast.success(
        !user.manage_treatments
          ? "Permesso 'Gestione listino' attivato"
          : "Permesso 'Gestione listino' disattivato",
      );
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  // Toggle flag "Modifica prezzo": permette all'utente di sovrascrivere
  // manualmente il prezzo (campo €) delle prestazioni marcate come
  // `price_editable=True` nel listino, su qualsiasi stage delle pipeline
  // "Assicurazioni Reparto Clinico" e "Assicurazioni Ortodonzia".
  // Richiesta utente 10/06/2026.
  const handleToggleModificaPrezzo = async (user) => {
    try {
      await axios.put(
        `${API}/api/admin/users/${user.user_id}/flags`,
        { modifica_prezzo: !user.modifica_prezzo },
        { withCredentials: true },
      );
      toast.success(
        !user.modifica_prezzo
          ? "Permesso 'Modifica prezzo' attivato"
          : "Permesso 'Modifica prezzo' disattivato",
      );
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  // Toggle flag "AI Assistant": abilita all'utente la creazione di preventivi
  // via chat GPT-5.4 dalla pagina Piano di Cura + accesso all'archivio
  // /preventivi. Richiesta utente 11/07/2026.
  const handleToggleAiAssistant = async (user) => {
    try {
      await axios.put(
        `${API}/api/admin/users/${user.user_id}/flags`,
        { ai_assistant: !user.ai_assistant },
        { withCredentials: true },
      );
      toast.success(
        !user.ai_assistant
          ? "Assistente AI (Preventivi) attivato"
          : "Assistente AI (Preventivi) disattivato",
      );
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Eliminare l'utente ${userName}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, { withCredentials: true });
      toast.success("Utente eliminato");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] bg-grid-light pt-[calc(7px+3.5rem+13px)]">
      <Toaster position="top-right" theme="light" />
      <div className="w-full px-4 lg:px-[100px] pb-8">
        <PageHeader
          title="Gestione Utenti"
          subtitle="Aggiungi, modifica o rimuovi gli utenti del sistema"
        />

        {/* Layout 2 colonne (richiesta utente Feb 2026):
            - Sinistra (lg:col-span-2 = 2/3): gestione utenti + matrice permessi
            - Destra (lg:col-span-1 = 1/3): pannelli sistema (chatbot,
              backup foto, backup DB, google reviews)
            Su mobile e tablet collassa a una colonna unica con i pannelli
            sistema in alto. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 -mt-8 relative z-10">
          {/* COLONNA SINISTRA — 2/3 — Gestione utenti + Permessi stage */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl p-4 sm:p-8 shadow-md space-y-6">
          <div className="flex justify-end gap-2 flex-wrap">
            <Button
              onClick={handleSyncGhl}
              disabled={isSyncingGhl}
              variant="outline"
              className="rounded-xl border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="sync-ghl-cache-btn"
            >
              {isSyncingGhl ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Aggiorna cache GHL
            </Button>
            <Button onClick={() => setShowForm(!showForm)} className="bg-[#3B42DD] hover:bg-[#2f36b8] text-white rounded-xl" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo Utente
            </Button>
            <Button onClick={handleSave} disabled={isSaving} variant="outline" className="rounded-xl border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="save-users-btn">
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva
            </Button>
          </div>

          {showForm && (
            <form onSubmit={handleCreate} className="bg-slate-50 rounded-2xl p-6 space-y-4 border border-slate-200" data-testid="create-user-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Nome</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome Cognome" className="h-11 bg-white rounded-xl" data-testid="new-user-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className="h-11 bg-white rounded-xl" data-testid="new-user-email" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Ruolo</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="h-11 bg-white rounded-xl" data-testid="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="user_plus">User+</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Info box: spiega all'admin il flusso di invito (24h,
                  Gmail vs non-Gmail). Nessun campo password da inserire. */}
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-[12px] text-blue-900 leading-relaxed" data-testid="invite-info-box">
                <div className="font-semibold mb-1">Flusso di attivazione</div>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Email <strong>non-Gmail</strong>: l&apos;utente riceverà un link valido <strong>24h</strong> per scegliere la propria password.</li>
                  <li>Email <strong>@gmail.com</strong>: l&apos;utente riceverà un&apos;email che lo invita ad accedere con il pulsante &quot;Accedi con Google&quot; (nessuna password).</li>
                </ul>
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-xl">Annulla</Button>
                <Button type="submit" disabled={isCreating} className="bg-[#3B42DD] hover:bg-[#2f36b8] text-white rounded-xl" data-testid="submit-new-user">
                  {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea Utente"}
                </Button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#3B42DD]" /></div>
          ) : users.length === 0 ? (
            <p className="text-center text-slate-500 py-12">Nessun utente registrato</p>
          ) : (
            <div className="space-y-4" data-testid="permissions-section">
              {/* Una CARD per OGNI utente (admin + non-admin).
                  Feb 2026 refactor: eliminata la lista compatta superiore
                  per ridurre la duplicazione visiva. Tutte le azioni
                  (ruolo, attivazione, elimina) sono nell'header grigio
                  della scheda. Gli admin hanno una scheda "ridotta" senza
                  griglia permessi (hanno accesso a tutto).
                  L'utente loggato non vede azioni su se stesso. */}
              <div className="px-1">
                <h3 className="text-sm font-bold text-slate-800">Utenti & Autorizzazioni</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Gestisci ruoli, attivazione, eliminazione e permessi pagine per ogni utente.
                </p>
              </div>

              {users.map((u) => {
                const allowedPages = u.allowed_pages || [];
                const isSelf = u.user_id === currentUser?.user_id;
                const isAdminUser = u.role === "admin";
                return (
                  <div
                    key={u.user_id}
                    className={`rounded-xl border overflow-hidden ${
                      u.is_active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"
                    }`}
                    data-testid={`user-card-${u.user_id}`}
                  >
                    {/* Header grigio — ANAGRAFICA + AZIONI */}
                    <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isAdminUser ? "bg-purple-100" : (u.role === "user_plus" ? "bg-violet-100" : "bg-blue-100")
                      }`}>
                        <User className={`w-4 h-4 ${
                          isAdminUser ? "text-purple-600" : (u.role === "user_plus" ? "text-violet-600" : "text-blue-600")
                        }`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-800 truncate">{u.name || "—"}</span>
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${
                            isAdminUser
                              ? "bg-purple-100 text-purple-700"
                              : (u.role === "user_plus" ? "bg-violet-100 text-violet-700" : "bg-blue-50 text-blue-600")
                          }`}>
                            {u.role}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                            u.auth_type === "google" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-600"
                          }`}>
                            {u.auth_type === "google" ? "Google" : "Email"}
                          </span>
                          {isSelf && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700">Tu</span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                      </div>
                      {!isAdminUser && (
                        <span className="text-[10px] font-bold text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-full flex-shrink-0">
                          {allowedPages.length}/{ALL_PAGES.length} pagine
                        </span>
                      )}
                      {!isSelf && (
                        <div className="flex items-center gap-2 flex-shrink-0" data-testid={`user-actions-${u.user_id}`}>
                          <Select
                            value={u.role}
                            onValueChange={(v) => handleRoleChange(u.user_id, v)}
                          >
                            <SelectTrigger className="h-8 w-28 rounded-lg text-xs bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="user_plus">User+</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          <button
                            onClick={() => handleToggleActive(u.user_id)}
                            className={`p-1.5 rounded-lg transition-colors bg-white border ${
                              u.is_active ? "text-green-600 border-green-200 hover:bg-green-50" : "text-slate-400 border-slate-200 hover:bg-slate-100"
                            }`}
                            title={u.is_active ? "Utente attivo — clicca per disattivare" : "Utente disattivato — clicca per riattivare"}
                            data-testid={`toggle-active-${u.user_id}`}
                          >
                            {u.is_active ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleDelete(u.user_id, u.name)}
                            className="p-1.5 rounded-lg text-red-500 bg-white border border-red-200 hover:bg-red-50 transition-colors"
                            title="Elimina utente"
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* CORPO — solo per utenti non-admin (gli admin hanno
                        accesso a tutto by design, niente permessi da
                        modificare; mostriamo un singolo box informativo). */}
                    {isAdminUser ? (
                      <div className="px-4 py-3 bg-purple-50/30">
                        <p className="text-[11px] text-purple-700 italic">
                          Accesso completo a tutte le pagine e funzionalità del sistema.
                        </p>
                      </div>
                    ) : (
                      <div className="px-4 py-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                          Pagine accessibili
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                          {ALL_PAGES.map((pg) => (
                            <label
                              key={pg.id}
                              className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded-md px-1.5 py-1 transition-colors"
                              data-testid={`page-${pg.id}-${u.user_id}`}
                            >
                              <Checkbox
                                checked={allowedPages.includes(pg.id)}
                                onCheckedChange={() => handlePageToggle(u.user_id, pg.id, allowedPages)}
                                className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 flex-shrink-0"
                              />
                              <span className="text-xs text-slate-700 truncate">{pg.label}</span>
                            </label>
                          ))}
                        </div>

                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-2">
                            Feature flag
                          </div>
                          <label
                            className="inline-flex items-center gap-2 cursor-pointer hover:bg-amber-50 rounded-md px-1.5 py-1 transition-colors"
                            data-testid={`flag-indiretti-${u.user_id}`}
                            title="Mostra i bottoni 'Ind.O.' e 'Ind.R.C.' in Archivio Piani di Cura per duplicare velocemente un piano in Indiretti"
                          >
                            <Checkbox
                              checked={!!u.indiretti_shortcut}
                              onCheckedChange={() => handleToggleIndirettiShortcut(u)}
                              className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                            />
                            <span className="text-xs text-slate-700">Indiretti rapidi</span>
                          </label>
                          <label
                            className="inline-flex items-center gap-2 cursor-pointer hover:bg-emerald-50 rounded-md px-1.5 py-1 transition-colors ml-2"
                            data-testid={`flag-manage-treatments-${u.user_id}`}
                            title="Permette all'utente di gestire il listino prestazioni (anche senza essere admin)"
                          >
                            <Checkbox
                              checked={!!u.manage_treatments}
                              onCheckedChange={() => handleToggleManageTreatments(u)}
                              className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                            />
                            <span className="text-xs text-slate-700">Gestione listino</span>
                          </label>
                          <label
                            className="inline-flex items-center gap-2 cursor-pointer hover:bg-sky-50 rounded-md px-1.5 py-1 transition-colors ml-2"
                            data-testid={`flag-modifica-prezzo-${u.user_id}`}
                            title="Permette di sovrascrivere a mano il prezzo (€) delle prestazioni marcate come modificabili nel listino, su qualsiasi stage delle pipeline Assicurazioni Reparto Clinico e Assicurazioni Ortodonzia"
                          >
                            <Checkbox
                              checked={!!u.modifica_prezzo}
                              onCheckedChange={() => handleToggleModificaPrezzo(u)}
                              className="data-[state=checked]:bg-sky-600 data-[state=checked]:border-sky-600"
                            />
                            <span className="text-xs text-slate-700">Modifica prezzo</span>
                          </label>
                          <label
                            className="inline-flex items-center gap-2 cursor-pointer hover:bg-sky-50 rounded-md px-1.5 py-1 transition-colors ml-2"
                            data-testid={`flag-ai-assistant-${u.user_id}`}
                            title="Abilita l'assistente AI (GPT-5.4) per generare bozze preventivi dalla pagina Piano di Cura e accedere all'archivio /preventivi"
                          >
                            <Checkbox
                              checked={!!u.ai_assistant}
                              onCheckedChange={() => handleToggleAiAssistant(u)}
                              className="data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600"
                            />
                            <span className="text-xs text-slate-700">Assistente AI</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </div>
          <StagePermissionsPanel />
          </div>
          {/* COLONNA DESTRA — 1/3 — Pannelli sistema (sticky su desktop
              così restano visibili scrollando la lista utenti) */}
          <aside className="lg:col-span-1 space-y-6 lg:sticky lg:top-[calc(7px+3.5rem+13px+24px)] lg:self-start">
            <ChatbotConfigPanel />
            <DriveBackupPanel />
            <GoogleBusinessPanel />
            <ListinoPriceAuditPanel />
            <AutoHealStatsPanel />
            <GhlResiliencePanel />
            <GhlWriteQueuePanel />
            <McpConnectorPanel />
          </aside>
        </div>
      </div>
    </div>
  );
}
